import type { Theme, ThemeOptions } from '@mui/material/styles';
import type { AppThemeTokens } from './branding/themeTypes';
import { PAGE_SIZE_OPTIONS } from './constants/AlertConstants';
import '@mui/x-date-pickers/themeAugmentation';
import '@mui/x-data-grid/themeAugmentation';
import '@mui/x-data-grid-pro/themeAugmentation';

// default theme for the current portal

const appTokens: AppThemeTokens = {
	ai: {
		gradient:
			'linear-gradient(90deg, #3B82F6 0%, #22C1C3 50%, #22C55E 100%)',
		accent: '#22C1C3',
		hoverShadow: '0 2px 10px rgba(34, 193, 195, 0.3)',
	},
	avatarColors: [
		'#1C3D49',
		'#AC4E00',
		'#005799',
		'#5C4300',
		'#6A1F32',
		'#2E6B4F',
		'#3A4C8C',
		'#8A5A00',
	],
	layout: {
		// headerHeight is the navigation offset only — the offset the docked
		// navigation rail sits below the top of the viewport. The rendered header is
		// taller: MUI's Toolbar `regular` variant enforces a 64px min-height at
		// >=600px, so on desktop the header is 64 + 6 = 70px and the drawer
		// deliberately overlaps it by 6px. This matches the pre-existing layout.
		// toolbarHeight is the `height` hint, which mixins.toolbar overrides on
		// desktop and which applies below 600px.
		//
		// Note there is deliberately no `contentOffset` here. Pages derive their
		// height from flexbox (see PageLayout -> PageContainer), which accounts for
		// every mixins.toolbar variant (64px >=600px, 56px below, 48px in mobile
		// landscape) plus the accent border for free. A single magic offset was
		// wrong at every width, and `100vh` arithmetic is unfixable on mobile.
		headerHeight: 64,
		toolbarHeight: 56,
		headerAccentHeight: 6,
		drawerWidthExpanded: 256,
		drawerWidthCollapsed: 64,
		detailPanelWidth: 520,
		// md (900px), not sm: below 900px neither the 256px rail nor a data grid
		// sitting beside a filter row is affordable, so sm would leave 600-899px in
		// a broken middle state.
		mobileBreakpoint: 'md',
		pageGutter: { xs: 2, sm: 2, md: 3 },
	},
	navigation: {
		selectedBackground: '#E3F2FD',
		selectedText: '#0D47A1',
	},
	surface: {
		dashboardShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
		// 4 * 4 = 16px, matching the cards' original `sx={{ borderRadius: 4 }}`.
		dashboardRadiusMultiplier: 4,
	},
	shape: {
		// Fully-rounded pill for surfaces MuiButton's override doesn't reach
		// (Card/Paper action bars, badges, ButtonBase). A px string, so `sx`
		// applies it verbatim instead of multiplying it by shape.borderRadius.
		pillRadius: '999px',
		// Severity accent stripe down the leading edge of a row or panel.
		accentStripeWidth: '4px',
	},
	typography: {
		/**
		 * Weights MUI's typography options don't name (fontWeightLight/Regular/
		 * Medium/Bold cover 300/400/500/700 only) but that recur across sx.
		 */
		fontWeightSemiBold: 600,
		fontWeightExtraBold: 800,
		fontWeightBlack: 900,
		// A single line sitting flush in its box: pill labels and stat figures
		// that have to align with an adjacent icon.
		lineHeightNone: 1,
		// Headings and large figures set tight, matching the 1.2 the metric and
		// label variants already use.
		lineHeightTight: 1.2,
		// Long-form copy: descriptions, AI summaries, wrapped dialog messages.
		// Matches the `code` variant's 1.6.
		lineHeightRelaxed: 1.6,
		// Tracking for uppercase captions and overlines. In em so it scales with
		// whatever font size the label is rendered at.
		letterSpacingLabel: '0.06em',
	},
};

export const currentPortalDefaultTheme: ThemeOptions = {
	app: appTokens,
	palette: {
		secondary: {
			main: '#FF8600',
		},
		severity: {
			primary: {
				critical: '#F4DDE3',
				high: '#FFE4CE',
				medium: '#FFF3C9',
				low: '#EDF8FF',
				undefined: '#EEEEEE',
			},
			secondary: {
				critical: '#FF0627',
				high: '#FF8C2E',
				medium: '#FDD132',
				low: '#7FCDFF',
				undefined: '#757575',
			},
			accent: {
				critical: '#6A1F32',
				high: '#AC4E00',
				medium: '#5C4300',
				low: '#005799',
				undefined: '#616161',
			},
		},
	},
	shape: {
		// MUI's default. Do not raise this: `sx` multiplies borderRadius by it,
		// so any change silently rescales ~100 existing call sites app-wide.
		borderRadius: 4,
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 50,
					textTransform: 'none',
				},
			},
		},
		MuiFormControl: {
			defaultProps: {
				variant: 'outlined',
				size: 'small',
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
				size: 'small',
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						backgroundColor: 'rgba(0, 0, 0, 0.08)',
						borderRadius: '10px',
						cursor: 'not-allowed',
					},
				},
				input: {
					'&.Mui-disabled': {
						WebkitTextFillColor: 'rgba(0, 0, 0, 0.65)',
						cursor: 'not-allowed',
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					borderRadius: '10px !important',
					'& input[type=number]::-webkit-inner-spin-button': {
						WebkitAppearance: 'none',
						margin: 0,
					},
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					borderRadius: '10px',
				},
			},
		},
		MuiTypography: {
			defaultProps: {
				variantMapping: {
					// Custom variants have no default mapping, so MUI falls back
					// to <span>. Only `display` needs correcting — it heads the
					// error pages and must not be a span.
					display: 'h1',
				},
			},
		},
		MuiTabScrollButton: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						display: 'none',
					},
				},
			},
		},
		// MuiTablePagination lives in getThemeComponents instead: its narrow-screen
		// override needs a breakpoint, and this options object has no theme in scope.
		MuiDataGrid: {
			defaultProps: {
				// Every grid in the app sets these three identically. A row is
				// 60px because it carries a severity chip and two lines of text,
				// and clicking a row always opens a drawer or detail page rather
				// than selecting it — selection is checkbox-only where it exists.
				rowHeight: 60,
				disableRowSelectionOnClick: true,
				pageSizeOptions: PAGE_SIZE_OPTIONS,
			},
		},
	},
	typography: {
		fontFamily: 'Roboto, sans-serif',
		h1: {
			// Replaces the `& h1` selector PageLayout used to scope over the
			// outlet. Same breakpoints, applied by the variant instead.
			fontSize: '1.5rem',
			'@media (min-width:900px)': {
				fontSize: '2.3rem',
			},
			fontWeight: 500,
		},
		// h2 and h4 carry no size of their own in MUI's scale terms — the
		// defaults (3.75rem / 2.125rem) are display sizes this app never wants.
		// Sandfield already overrode both; defining them here gives every brand
		// the same heading scale instead of leaving Mainfreight on the defaults.
		h2: {
			fontSize: '1.75rem',
			fontWeight: 500,
		},
		h3: {
			fontSize: '1.5rem',
		},
		h4: {
			// A step below h3 so the ramp descends: h3 and h4 both sat at 1.5rem
			// and read as the same level wherever they appeared together.
			fontSize: '1.25rem',
			fontWeight: 400,
		},
		h5: {
			// Only a size, so brands keep their own h5 treatment: Sandfield's
			// override (colour, weight, tighter line-height) still wins. Without
			// this Mainfreight sits on MUI's 1.5rem default, which since h4
			// dropped to 1.25rem would render h5 *larger* than h4.
			fontSize: '1.125rem',
		},
		h6: {
			// Completes the ramp. MUI's default h6 is 1.25rem, which since h5
			// dropped to 1.125rem would render h6 *larger* than h5 - and equal
			// to h4. h6 is the most-used heading in this app (19 sites against
			// h5's 3), so the inversion showed up far more often than h5 did.
			// Weight is left at MUI's 500 so only the size changes.
			fontSize: '1rem',
		},
		// Dashboard metric label — matches the h4 default weight it replaced.
		metric: {
			fontSize: '1.375rem',
			fontWeight: 400,
			lineHeight: appTokens.typography.lineHeightTight,
			letterSpacing: '-0.09375rem',
		},
		// The metric figure itself, rendered lighter than its label.
		metricValue: {
			fontSize: '1.375rem',
			fontWeight: 300,
			lineHeight: appTokens.typography.lineHeightTight,
			letterSpacing: '-0.09375rem',
		},
		label: {
			fontSize: '0.75rem',
			fontWeight: 700,
			lineHeight: appTokens.typography.lineHeightTight,
			letterSpacing: appTokens.typography.letterSpacingLabel,
		},
		code: {
			fontFamily: 'monospace',
			fontSize: '0.875rem',
			lineHeight: appTokens.typography.lineHeightRelaxed,
		},
		display: {
			fontSize: '3.5rem',
			fontWeight: 700,
			lineHeight: appTokens.typography.lineHeightNone,
		},
	},
	portalFramework: {
		pages: {
			login: {
				backgroundContainer: {
					sx: {
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						zIndex: -1,
					},
				},
			},
		},
	},
};

// for generating theme component overrides based on the current theme
export const getThemeComponents = (theme: Theme) => ({
	MuiAppBar: {
		styleOverrides: {
			root: {
				color: theme.palette.text.primary,
			},
		},
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: {
				'&:hover .MuiOutlinedInput-notchedOutline': {
					borderColor: theme.palette.primary.main,
				},
			},
		},
	},
	MuiCssBaseline: {
		styleOverrides: {
			'html, body, #root': {
				minHeight: '100%',
				margin: 0,
				padding: 0,
			},
			'*': {
				scrollbarWidth: 'thin',
			},
		},
	},
	MuiListItemButton: {
		styleOverrides: {
			root: {
				margin: theme.spacing(0.5, 1),
				// styleOverrides is plain CSS, so this is raw px, not a
				// multiplier. 4 * 2 = 8px, matching master's literal '8px'.
				borderRadius: Number(theme.shape.borderRadius) * 2,
				paddingLeft: theme.spacing(2),
				'& .MuiListItemIcon-root': {
					color: theme.palette.text.primary,
				},
				'&.Mui-selected': {
					backgroundColor: theme.app.navigation.selectedBackground,
					color: theme.app.navigation.selectedText,
					'&:hover': {
						backgroundColor:
							theme.app.navigation.selectedBackground,
					},
					'& .MuiListItemIcon-root': {
						color: theme.app.navigation.selectedText,
					},
				},
			},
		},
	},
	MuiTablePagination: {
		styleOverrides: {
			root: {
				overflow: 'hidden',
				// DataGrid renders TablePagination internally, so a flat
				// overflow:hidden clipped the footer — page-size select, row count
				// and next/prev arrows all unreachable — on every grid in the app
				// at narrow widths. Reachable beats tidy below sm.
				[theme.breakpoints.down('sm')]: {
					overflow: 'auto',
				},
			},
			toolbar: {
				[theme.breakpoints.down('sm')]: {
					flexWrap: 'wrap' as const,
					height: 'auto',
					minHeight: 0,
					rowGap: theme.spacing(1),
					paddingLeft: theme.spacing(1),
				},
			},
		},
	},
	MuiDialog: {
		styleOverrides: {
			// Full-screen below sm, in CSS rather than the `fullScreen` prop.
			// Solved globally on purpose: two of the app's dialogs are declared
			// inline at their call sites and more will be added, a CSS override
			// cannot be forgotten, needs no hook (so it cannot desync from the JS
			// branch), and it reaches @sal/portal's dialogs which we cannot edit.
			//
			// Callers passing `sx` to the paper slot outrank this in emotion's
			// ordering — see ConfirmDialog, which has to spell out its own
			// { xs: 0, sm: 3 } radius for that reason.
			paper: {
				[theme.breakpoints.down('sm')]: {
					margin: 0,
					width: '100%',
					maxWidth: '100%',
					height: '100%',
					maxHeight: '100%',
					borderRadius: 0,
				},
			},
		},
	},
	MuiDataGrid: {
		styleOverrides: {
			// The filter/columns panel is ~400px and would otherwise overflow the
			// viewport on every grid.
			panel: {
				[theme.breakpoints.down('sm')]: {
					maxWidth: '100vw',
				},
			},
		},
	},
	MuiCard: {
		variants: [
			{
				props: { variant: 'dashboard' as const },
				style: {
					display: 'flex',
					flexDirection: 'column' as const,
					padding: theme.spacing(3),
					borderRadius:
						Number(theme.shape.borderRadius) *
						theme.app.surface.dashboardRadiusMultiplier,
					border: `1px solid ${theme.palette.divider}`,
					boxShadow: theme.app.surface.dashboardShadow,
					backgroundColor: theme.palette.background.paper,
				},
			},
		],
	},
});
